import { formatCents } from '@src/common';
import { DetailedSaleItem, ProductPackage } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import React, { useMemo } from 'react';

import { CustomAccordion } from '@planne-software/uni/Accordion';
import {
  PackageProductWrapper,
  Product,
  PackagePriceInfo,
  PackageOriginalPrice,
  PackageDiscountValue,
  PackageTotalPrice,
  PackagePrices,
  PackageTariffInfo,
  ProductName,
  PackageDates,
  PackageProductDetails,
  PackageProductDetailsContent,
} from '@src/components/Checkout/styles';
import PackageItemDetail from './ProductPackageItem';
import { calculatePackageTotal } from '@src/domains/cart';
import { getFormattedTariffs } from '@src/features/orders/core/getFormattedTariffs';
import { getPackageDates } from '@src/features/orders/core/getPackageDates';
import useShowItemPrices from '@src/hooks/useShowItemPrices';

interface PackageItemGroupProps {
  productPackage: ProductPackage;
  items: DetailedSaleItem[];
}

const ProductPackageGroup = (props: PackageItemGroupProps) => {
  const { productPackage, items } = props;

  const { i18n, locale } = useTranslate();

  const packageDates = useMemo(() => getPackageDates(items, locale), [items, locale]);
  const formattedTariffs = useMemo(() => getFormattedTariffs(items), [items]);

  const fullPriceCentsPackage = useMemo(() => {
    return calculatePackageTotal(items, (item) => item.fullPriceCurrencyInfo.amountCents);
  }, [items]);

  const totalPriceCentsPackage = useMemo(() => {
    return calculatePackageTotal(items, (item) => item.totalPriceCurrencyInfo.amountCents);
  }, [items]);

  const showItemPrices = useShowItemPrices();

  return (
    <Product key={`${productPackage.name}-${productPackage.id}`}>
      <ProductName>{productPackage.name}</ProductName>

      {packageDates && (
        <PackageDates>
          {i18n('cart_package_date')} {packageDates}
        </PackageDates>
      )}

      <PackageTariffInfo>
        {i18n('cart_package_tariff')} {formattedTariffs}
      </PackageTariffInfo>

      {showItemPrices ? (
        <PackagePrices>
          <PackagePriceInfo>
            {i18n('cart_package_previous_price')}
            <PackageOriginalPrice>{formatCents(fullPriceCentsPackage)}</PackageOriginalPrice>
          </PackagePriceInfo>

          <PackagePriceInfo>
            {i18n('cart_package_discount')}
            <PackageDiscountValue>-{formatCents(fullPriceCentsPackage - totalPriceCentsPackage)}</PackageDiscountValue>
          </PackagePriceInfo>

          <PackagePriceInfo>
            {i18n('cart_package_total')}
            <PackageTotalPrice>{formatCents(totalPriceCentsPackage)}</PackageTotalPrice>
          </PackagePriceInfo>
        </PackagePrices>
      ) : null}

      <CustomAccordion
        title={<PackageProductDetails>{i18n('cart_package_product_details')}</PackageProductDetails>}
        showBorderBottom
        content={
          <PackageProductDetailsContent>
            <PackageProductWrapper>
              {items.map((item) => (
                <PackageItemDetail key={item.id} item={item} />
              ))}
            </PackageProductWrapper>
          </PackageProductDetailsContent>
        }
      />
    </Product>
  );
};

export default ProductPackageGroup;
